import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ClassifyAsContributionFields,
  ClassifiedRefundDto,
  ClassifyAsExpenditureFields,
  ExcludeFromReportFields,
  ExcludeFromReportResponse,
  ProcessorFeeCheckResponse,
  ReopenBankTransactionRequest,
  ReopenBankTransactionResponse,
  ContributionDto,
  ExpenditureDto,
  CapturedReceiptSuggestionDto,
  TriageItem,
} from '@signalvote/shared';
import {
  classifyAsContributionRequest,
  classifyAsExpenditureRequest,
  excludeFromReportRequest,
  ErrorCode,
  ExpenditureClassificationSubCategory,
} from '@signalvote/shared';
import { apiJson, apiResponse } from './apiFetch';
import { queryKeys } from '@/lib/queryKeys';
import { throwStagedRowError } from './useFinanceContributions';
import {
  markBankExcluded,
  markBankReopened,
  reconcileSortInboxBankLineVersion,
  rowMutationScope,
  type LedgerMutationOptions,
} from './sortInboxOptimistic';
import { useOptimisticStatusFlip } from './optimisticStatusFlip';

export type { LedgerMutationOptions };

/**
 * Kind predicate selecting a BankLine row in the sort-inbox cache — the row
 * identity the optimistic patch and the concurrency-token write-back both key on
 * (the exclude → reopen → exclude loop's follow-ups read the reconciled version).
 */
const bankTransactionRow = (btId: string) => (item: TriageItem): boolean =>
  item.kind === 'BankLine' && item.bt.id === btId;

/** Thrown when the server returns a 409 — another client touched the BankTransaction. */
export class BankTransactionConcurrencyConflictError extends Error {
  // Always a 409; carried so the transient-vs-terminal split (ledgerActionRetry)
  // reads this HTTP response as terminal rather than a retriable network blip.
  status = 409;
  // These endpoints send some 409s with no code, so the class names the stale write itself for isStaleWrite.
  readonly code = ErrorCode.ConcurrencyConflict;
  constructor(message: string) {
    super(message);
    this.name = 'BankTransactionConcurrencyConflictError';
  }
}

/**
 * Thrown when the server returns a 4xx with a stable error `code`
 * (e.g. `BankTransactionAlreadyClassified`, `ContactNotFound`). The panel
 * surfaces the message verbatim and may switch on `code` for follow-ups.
 */
export class BankTransactionClassifyError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'BankTransactionClassifyError';
    this.code = code;
    this.status = status;
  }
}

async function readErrorBody(response: Response): Promise<{ code?: string; error?: string }> {
  return response.json().catch(() => ({}));
}

function invalidateClassificationSurfaces(
  queryClient: ReturnType<typeof useQueryClient>,
  options: { contributions?: boolean; expenditures?: boolean } = {},
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.reconciliation.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
  // CPH-005: bank-tx classification shifts per-source unattributed counts +
  // tied flags on the 3-phase tracker. The close.all prefix covers both
  // `current` and `phase-state` keys. Only `invalidateQueries` touches this
  // prefix; if a future caller adds `setQueriesData`, it MUST guard with
  // `if (!old || !('items' in old)) return old;` per the Golden Rules —
  // close-hub responses aren't list-shaped.
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.close.all });
  if (options.contributions) {
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
  }
  if (options.expenditures) {
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
  }
}

/**
 * onSettled invalidations shared by the two bank status flips (exclude / reopen):
 * the classification surfaces plus the campaign-scoped sort inbox (the row
 * re-renders born-Done / back-to-NeedsAction). Deliberately touches NO money
 * ledger (exclude/reopen create no Contribution/Expenditure) — the negative is
 * pinned by `useExcludeFromReport.test.ts`.
 */
function invalidateBankStatusFlip(queryClient: QueryClient): void {
  invalidateClassificationSurfaces(queryClient);
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
}

export function useClassifyBankTransactionAsContribution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ClassifyAsContributionFields }) => {
      const response = await apiResponse(`/api/finance/bank-transactions/${id}/classify-as-contribution`, {
        method: 'POST',
        json: classifyAsContributionRequest(data),
      });
      if (response.status === 409) {
        const body = await readErrorBody(response);
        throw new BankTransactionConcurrencyConflictError(
          body.error ?? 'This bank line was modified by another user. Refreshing.',
        );
      }
      if (!response.ok) {
        const body = await readErrorBody(response);
        throw new BankTransactionClassifyError(
          body.code ?? 'Unknown',
          response.status,
          body.error ?? 'Failed to classify bank transaction as contribution',
        );
      }
      return response.json() as Promise<ContributionDto>;
    },
    onSettled: () => invalidateClassificationSurfaces(queryClient, { contributions: true }),
  });
}

export function useClassifyBankTransactionAsExpenditure(options: LedgerMutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    scope: rowMutationScope(options.scopeId),
    mutationFn: async ({ id, data }: { id: string; data: ClassifyAsExpenditureFields }) => {
      const response = await apiResponse(`/api/finance/bank-transactions/${id}/classify-as-expenditure`, {
        method: 'POST',
        json: classifyAsExpenditureRequest(data),
      });
      if (response.status === 409) {
        const body = await readErrorBody(response);
        throw new BankTransactionConcurrencyConflictError(
          body.error ?? 'This bank line was modified by another user. Refreshing.',
        );
      }
      if (!response.ok) {
        const body = await readErrorBody(response);
        throw new BankTransactionClassifyError(
          body.code ?? 'Unknown',
          response.status,
          body.error ?? 'Failed to classify bank transaction as expenditure',
        );
      }
      // RFR-002: the Refund sub-category returns a ContributionRefund shape
      // (no Expenditure); the other three return an ExpenditureDto.
      return response.json() as Promise<ExpenditureDto | ClassifiedRefundDto>;
    },
    // Write the bank line's freshly-bumped token back so a rapid follow-up on the same row sends
    // the current version rather than the one it classified with.
    onSuccess: (result, variables) => {
      if ('bankTransactionConcurrencyVersion' in result) {
        reconcileSortInboxBankLineVersion(
          queryClient,
          bankTransactionRow(variables.id),
          result.bankTransactionConcurrencyVersion,
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      // RFR-002: a refund nets the donor's limit-totals (contacts) and surfaces
      // in the All-Time Ledger via the Refund source (sort-inbox), not the
      // expenditures surface. The other three sub-categories invalidate
      // expenditures + contacts as before.
      if (variables?.data.subCategory === ExpenditureClassificationSubCategory.Refund) {
        invalidateClassificationSurfaces(queryClient);
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
        return;
      }
      invalidateClassificationSurfaces(queryClient, { expenditures: true });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * EFR-002 / ADR-014 — "Exclude from report". POSTs to
 * `/api/finance/bank-transactions/{id}/exclude-from-report`, flipping an
 * Unattributed bank line to `ExcludedFromReport` — the single non-reporting
 * resolution that collapses the retired Dismiss / Mark-as-transfer /
 * Pair-as-transfer flows (none affected any rollup or filing). Carries one
 * optional free-text note.
 *
 * Error envelope mirrors the reopen hook: 409 →
 * {@link BankTransactionConcurrencyConflictError}; other 4xx →
 * {@link BankTransactionClassifyError} with the wire `code` (one of
 * `BankTransactionExcludeFromReport*` codes, `'Unknown'` fallback) — a
 * server `NoteTooLong` (400) surfaces its message this way. `onSettled`
 * invalidates the bank-row surfaces + the campaign-scoped sort-inbox so the
 * row re-renders born-Done in the "Not reported" lens. Invalidation contract
 * pinned by `__tests__/useExcludeFromReport.test.ts`.
 */
export function useExcludeBankTransactionFromReport(options?: LedgerMutationOptions) {
  return useOptimisticStatusFlip({
    scopeId: options?.scopeId,
    endpoint: {
      entity: 'BankTransaction',
      send: async (id, concurrencyVersion, fields: Omit<ExcludeFromReportFields, 'concurrencyVersion'>) => {
        const response = await apiResponse(`/api/finance/bank-transactions/${id}/exclude-from-report`, {
          method: 'POST',
          json: excludeFromReportRequest({ concurrencyVersion, ...fields }),
        });
        if (response.status === 409) {
          const body = await readErrorBody(response);
          throw new BankTransactionConcurrencyConflictError(
            body.error ?? 'This bank line was modified by another user. Refreshing.',
          );
        }
        if (!response.ok) {
          const body = await readErrorBody(response);
          throw new BankTransactionClassifyError(
            body.code ?? 'Unknown',
            response.status,
            body.error ?? 'Failed to exclude bank transaction from report',
          );
        }
        return response.json() as Promise<ExcludeFromReportResponse>;
      },
    },
    // Stamp the bank line "Not reported" (Done/Excluded) on click; the row stays in
    // place until the onSettled refetch reconciles server truth.
    patch: markBankExcluded,
    invalidateKeys: invalidateBankStatusFlip,
  });
}

/**
 * Bank-fee-line steering (spec §D7): resolves the payment processor for the bank
 * line and checks whether a fee for that processor is already booked in the period.
 * Only fetches when `enabled` is true (i.e., "Processing fee" is selected).
 * Returns null/loading state while fetching.
 */
export function useProcessorFeeCheck(
  bankTransactionId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.finance.bankTransactions.processorFeeCheck(bankTransactionId),
    queryFn: () =>
      apiJson<ProcessorFeeCheckResponse>(
        `/api/finance/bank-transactions/${bankTransactionId}/processor-fee-check`,
        { error: 'Failed to check processor fee status' },
      ),
    enabled,
    staleTime: 30_000, // 30 s — the booked-fee state won't change mid-session
  });
}

/**
 * CAP-006 — the pocket-receipt suggest-match. Fetches the Unattached captured
 * receipts that plausibly back this bank debit (exact amount + date within ±7
 * days, resolved server-side) from
 * GET /api/finance/bank-transactions/{id}/receipt-suggestions.
 *
 * Only fetches when `enabled` is true — the classify panel enables it only
 * while the Expenditure path is selected on a debit row, so a contribution /
 * exclude classification never issues the request. An empty
 * array (no pocket match) is the common case and renders nothing.
 *
 * The treasurer picks a suggestion to attach; a suggestion is NEVER
 * auto-attached (finance product direction — suggest, don't auto-apply).
 */
export function useReceiptSuggestions(bankTransactionId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.finance.bankTransactions.receiptSuggestions(bankTransactionId),
    queryFn: () =>
      apiJson<CapturedReceiptSuggestionDto[]>(
        `/api/finance/bank-transactions/${bankTransactionId}/receipt-suggestions`,
        { error: 'Failed to load receipt suggestions' },
      ),
    enabled,
    staleTime: 30_000, // 30 s — the pocket won't change mid-session
  });
}

/**
 * ATL-005 / ATL-006 — "Reopen" a resolved bank row from the all-time ledger.
 * POSTs to `/api/finance/bank-transactions/{id}/reopen`, flipping an
 * ExcludedFromReport (ADR-014) or DepositOfRecordedChecks row back to
 * Unattributed so the normal classify buttons take over. Single-row (ADR-014).
 *
 * Error envelope (per the ATL-005 controller): 409 carries no wire code
 * (concurrency — surfaced via {@link BankTransactionConcurrencyConflictError});
 * 400 carries one of exactly two codes (`AlreadyClassified` / `NotReopenable`)
 * the caller switches on for treasurer copy; 404 carries no code. Non-409
 * failures throw {@link BankTransactionClassifyError} with the wire `code`
 * (the sibling `'Unknown'` fallback when absent, e.g. a 404 — the caller's
 * error switch falls through to the message in that case).
 *
 * Invalidates the campaign-scoped sort-inbox key specifically (the row must
 * re-render as NeedsAction / Classify) plus the bank-row surfaces, not every
 * finance query through `financeQueries`.
 */
export function useReopenBankTransaction(options?: LedgerMutationOptions) {
  return useOptimisticStatusFlip({
    scopeId: options?.scopeId,
    endpoint: {
      entity: 'BankTransaction',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(`/api/finance/bank-transactions/${id}/reopen`, {
          method: 'POST',
          json: { concurrencyVersion } satisfies ReopenBankTransactionRequest,
        });
        if (response.status === 409) {
          const body = await readErrorBody(response);
          throw new BankTransactionConcurrencyConflictError(
            body.error ?? 'This bank line was modified by another user. Refreshing.',
          );
        }
        if (!response.ok) {
          const body = await readErrorBody(response);
          throw new BankTransactionClassifyError(
            body.code ?? 'Unknown',
            response.status,
            body.error ?? 'Failed to reopen bank transaction',
          );
        }
        return response.json() as Promise<ReopenBankTransactionResponse>;
      },
    },
    // Reopen instantly (Done/Excluded → NeedsAction/Classify), back in the
    // un-triaged queue; onSettled reconciles server truth.
    patch: markBankReopened,
    invalidateKeys: invalidateBankStatusFlip,
  });
}

/**
 * ADR-016 step 2 — permanent single-row delete of a bank transaction, fired
 * from the ledger row's detail. Mirrors the money-row deletes: 204 (no payload),
 * the concurrency token rides as a query param (DELETE carries no body), and it
 * reuses the `StagedRowUpdateError` envelope so the detail dialog translates the
 * refusal codes uniformly (`DeleteBankLineClassified` → 409, etc.).
 *
 * ADR-006 amendment: a raw bank line may now be deleted by a treasurer. There is
 * no tombstone, so re-importing the same statement can resurrect it — the detail
 * confirm says so, so the removal is never a silent surprise.
 */
export function useDeleteBankTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      concurrencyVersion,
    }: {
      id: string;
      concurrencyVersion: number;
    }) => {
      const response = await apiResponse(
        `/api/finance/bank-transactions/${id}?concurrencyVersion=${concurrencyVersion}`,
        { method: 'DELETE' },
      );
      if (!response.ok) {
        await throwStagedRowError(response, 'Failed to delete bank transaction');
      }
      // 204 No Content — no payload.
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}
